import Head from 'next/head';
import Header from '../../../../components/Header';
import Footer from '../../../../components/Footer';
import Hero from '../../../../components/Hero';
import geoCoverage from '../../../../data/geoCoverage';
import { generateWebPageSchema } from '../../../../utils/generateWebPageSchema';

const SERVICES = [
  'complete-home-remodeling',
  'kitchen-remodeling',
  'bathroom-remodeling',
  'adu',
  'exterior-painting',
  'earthquake-retrofit',
  'foundations',
  'pavers',
  'outdoor-living',
  'gutter',
  'energy-efficient-windows',
  'solar-panels',
  'artificial-turf',
  'hvac',
  'asphalt-shingles',
  'tile-roof',
  'flat-roof',
  'metal-roof',
  'tpo-roof',
];

const format = (str) =>
  str.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function CityZips({ service, city }) {
  const baseUrl = 'https://modernbuildremodel.com';
  const formattedService = format(service);
  const formattedCity = format(city);
  const pageUrl = `${baseUrl}/sitemap/services/${service}/${city}`;

  const zips = geoCoverage.california[city] || [];

  /* =========================
     Breadcrumb Schema
  ========================== */
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Sitemap", "item": `${baseUrl}/sitemap` },
      { "@type": "ListItem", "position": 3, "name": "Services", "item": `${baseUrl}/sitemap/services` },
      { "@type": "ListItem", "position": 4, "name": formattedService, "item": `${baseUrl}/sitemap/services/${service}` },
      { "@type": "ListItem", "position": 5, "name": formattedCity, "item": pageUrl }
    ]
  };

  return (
    <>
      <Head>
        <title>{formattedService} Services in {formattedCity} | Modern Build</title>
        <meta name="robots" content="index, follow" />
        <meta name="title" content={`${formattedService} Services in ${formattedCity} | Modern Build`} />
        <meta
          name="description"
          content={`Explore all ${formattedService} services available in ${formattedCity}, California. View detailed ZIP-specific service pages.`}
        />
        <link rel="canonical" href={pageUrl} />

        <meta name="author" content="Modern Build Home Remodeling" />

        {/* Open Graph */}
        <meta property="og:title" content={`${formattedService} Services in ${formattedCity} | Modern Build`} />
        <meta property="og:description" content={`Browse ${formattedService} locations in ${formattedCity} with access to ZIP-specific service pages.`} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${formattedService} Services in ${formattedCity} | Modern Build`} />
        <meta name="twitter:description" content={`Find ${formattedService} services in ${formattedCity}, California.`} />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />
        
        {/* WebPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: `${formattedService} Services in ${formattedCity}`,
                description: `Browse all ${formattedService} services in ${formattedCity}, California with ZIP-specific service pages.`,
                slug: `/sitemap/services/${service}/${city}`
              })
            )
          }}
        />

        {/* Breadcrumb */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
      </Head>

      <Header />
      <Hero category="sitemap" page="zipcodes" hasContentH1={true} />

      <main className="container mx-auto px-4 py-10 max-w-6xl">
        <h1 className="text-3xl font-gilda mb-4">
          {formattedService} Services in {formattedCity} ZIP Codes
        </h1>

        <p className="mb-6 text-gray-600">
          Select a ZIP code to view service pages for {formattedService} in {formattedCity}.
        </p>

        <ul className="grid md:grid-cols-3 gap-3">
          {zips.map((zip) => (
            <li key={zip}>
              <a
                href={`/${service}/california/${city}/${zip}`}
                className="text-primary hover:underline"
              >
                {formattedService} {formattedCity} {zip}
              </a>
            </li>
          ))}
        </ul>
      </main>

      <Footer />
    </>
  );
}

/* =========================
   STATIC GENERATION
========================= */

export async function getStaticPaths() {
  const paths = [];

  SERVICES.forEach((service) => {
    Object.keys(geoCoverage.california).forEach((city) => {
      paths.push({
        params: { service, city },
      });
    });
  });

  return {
    paths,
    fallback: false, // ensure all pages are server-rendered
  };
}

export async function getStaticProps({ params }) {
  return {
    props: {
      service: params.service,
      city: params.city,
    },
  };
}